/**
 * Header Layout store — drives the configurable top-header (Header Builder).
 *
 * The store holds the EFFECTIVE layout for the current user (personal override
 * -> company -> built-in default) plus the element catalog. App.vue consults
 * the tiny helpers below to decide, per element, whether it is shown directly
 * in the bar, tucked into the "⋯" overflow menu, hidden, and in what order.
 *
 * IMPORTANT — zero-risk fallback: the layout is only applied when the
 * "New Design" preset is active (branding.iconStyle === 'newdesign'). When it
 * is off, every helper reports the legacy behaviour (show everything inline,
 * source order), so the existing header renders byte-for-byte as before.
 */
import { defineStore } from 'pinia'
import headerBuilderApi from '@/api/headerBuilder'
import { useBrandingStore } from '@/stores/branding'

// Built-in default — mirrors backend header_service.default_layout(). Used as a
// safety net before the API responds so helpers never throw.
const DEFAULT_ITEMS = [
  { key: 'search', zone: 'header', order: 0, visible: true },
  { key: 'presence', zone: 'header', order: 1, visible: true },
  { key: 'quick_create', zone: 'header', order: 2, visible: true },
  { key: 'mute', zone: 'header', order: 3, visible: true },
  { key: 'notifications', zone: 'header', order: 4, visible: true },
  { key: 'task_notifications', zone: 'header', order: 5, visible: true },
  { key: 'chat', zone: 'header', order: 6, visible: true },
  { key: 'dialer', zone: 'header', order: 7, visible: true },
  { key: 'call_alerts', zone: 'header', order: 8, visible: true },
  { key: 'balance', zone: 'header', order: 9, visible: true },
  { key: 'ai', zone: 'header', order: 10, visible: true },
]

// Divider width bounds (px) — mirror backend header_service.
const DEFAULT_SPACER_WIDTH = 16
const MIN_SPACER_WIDTH = 4
const MAX_SPACER_WIDTH = 400

export const useHeaderLayoutStore = defineStore('headerLayout', {
  state: () => ({
    layout: { items: DEFAULT_ITEMS.map((x) => ({ ...x })) },
    catalog: [],
    source: 'default',
    canEditCompany: false,
    loaded: false,
    loading: false,
    // Live-preview override used by the builder while editing (not persisted).
    preview: null,
    // On-header live edit mode: true while the Header Builder drawer is open.
    // Drives the dashed frames / grips / eye toggles rendered ON the real header.
    editing: false,
    // Bumped every time the preview draft changes, so the on-header overlay can
    // resync its local drag arrays when the change comes from elsewhere
    // (e.g. a template applied from the drawer).
    previewVersion: 0,
  }),

  getters: {
    // The layout the header should actually reflect right now.
    effectiveItems(state) {
      const src = state.preview?.items || state.layout?.items || DEFAULT_ITEMS
      return src
    },
    byKey() {
      const map = {}
      for (const it of this.effectiveItems) map[it.key] = it
      return map
    },
    // Catalog metadata (core / more_allowed) by key, for the editors.
    metaByKey(state) {
      const map = {}
      for (const c of (state.catalog || [])) map[c.key] = c
      return map
    },
    // Current arrangement split into the three zones (ordered), for editors.
    // Spacers are excluded here — they are decorations handled separately.
    zonesSnapshot() {
      const items = this.effectiveItems.slice().sort((a, b) => a.order - b.order)
      const ctrl = (i) => i.key !== 'spacer'
      return {
        header: items.filter((i) => i.zone === 'header' && ctrl(i)).map((i) => i.key),
        more: items.filter((i) => i.zone === 'more' && ctrl(i)).map((i) => i.key),
        hidden: items.filter((i) => i.zone === 'hidden' && ctrl(i)).map((i) => i.key),
      }
    },
    // Ordered header row as a list of "iids": a control key, or "spacer:<id>"
    // for a divider. This is the single source of truth for on-header reorder.
    headerIids() {
      return this.effectiveItems
        .filter((i) => i.zone === 'header')
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((i) => (i.key === 'spacer' ? `spacer:${i.id}` : i.key))
    },
    moreKeys() { return this.zonesSnapshot.more },
    hiddenKeys() { return this.zonesSnapshot.hidden },
    // Divider instances currently in the header (for rendering). Only under the
    // New Design preset — the legacy header never shows builder dividers.
    spacerItems() {
      if (!this.active) return []
      return this.effectiveItems
        .filter((i) => i.key === 'spacer' && i.zone === 'header')
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((i) => ({ id: i.id, order: i.order, width: i.width || DEFAULT_SPACER_WIDTH }))
    },
    // Only honour the layout under the New Design preset.
    active() {
      let isNewDesign = false
      try { isNewDesign = useBrandingStore().isNewDesignIcons } catch { /* ignore */ }
      return isNewDesign && (this.loaded || !!this.preview)
    },
  },

  actions: {
    _item(key) { return this.byKey[key] },

    metaOf(key) { return this.metaByKey[key] || { core: false, more_allowed: false } },

    // On-header edit mode lifecycle (wired to the drawer open/close).
    setEditing(v) { this.editing = !!v },

    // Rebuild the live-preview draft from an ordered header "iid" sequence
    // (control keys and "spacer:<id>" dividers) plus the more/hidden control
    // lists. Single build path for the whole builder.
    applySeq(headerIids, moreKeys, hiddenKeys) {
      // Preserve each divider's configured width across reorders — headerIids
      // only carries the id, so without this a move/hide would reset the width.
      const widthById = {}
      for (const it of this.effectiveItems) {
        if (it.key === 'spacer') widthById[it.id] = it.width || DEFAULT_SPACER_WIDTH
      }
      const items = []
      let order = 0
      for (const iid of (headerIids || [])) {
        if (typeof iid === 'string' && iid.startsWith('spacer:')) {
          const sid = iid.slice(7)
          items.push({ key: 'spacer', id: sid, zone: 'header', order: order++, visible: true, width: widthById[sid] || DEFAULT_SPACER_WIDTH })
        } else {
          items.push({ key: iid, zone: 'header', order: order++, visible: true })
        }
      }
      for (const k of (moreKeys || [])) items.push({ key: k, zone: 'more', order: order++, visible: true })
      for (const k of (hiddenKeys || [])) items.push({ key: k, zone: 'hidden', order: order++, visible: false })
      this.setPreview({ items })
    },

    // Move an iid (control key or "spacer:<id>") within the header row to just
    // before/after another iid. Used by the on-header drag.
    moveIid(sourceIid, targetIid, before) {
      if (!sourceIid || sourceIid === targetIid) return
      const seq = this.headerIids.filter((x) => x !== sourceIid)
      const idx = seq.indexOf(targetIid)
      if (idx === -1) seq.push(sourceIid)
      else seq.splice(before ? idx : idx + 1, 0, sourceIid)
      this.applySeq(seq, this.moreKeys, this.hiddenKeys)
    },

    // Move an iid one step left (dir=-1) or right (dir=+1) within the header
    // row. Backs the ←/→ arrow controls (a reliable, no-drag reorder). Returns
    // true if it moved, false if already at the edge.
    nudgeIid(iid, dir) {
      const seq = this.headerIids.slice()
      const idx = seq.indexOf(iid)
      if (idx === -1) return false
      const to = idx + (dir < 0 ? -1 : 1)
      if (to < 0 || to >= seq.length) return false
      seq.splice(to, 0, seq.splice(idx, 1)[0])
      this.applySeq(seq, this.moreKeys, this.hiddenKeys)
      return true
    },

    // Insert a new divider into the header row (at end, or before/after an iid).
    addSpacer(targetIid = null, before = false) {
      const id = `s${Date.now().toString(36)}${Math.floor(Math.random() * 1000)}`
      const seq = this.headerIids.slice()
      const iid = `spacer:${id}`
      if (targetIid) {
        const idx = seq.indexOf(targetIid)
        if (idx === -1) seq.push(iid)
        else seq.splice(before ? idx : idx + 1, 0, iid)
      } else {
        seq.push(iid)
      }
      this.applySeq(seq, this.moreKeys, this.hiddenKeys)
    },

    removeSpacer(id) {
      const seq = this.headerIids.filter((x) => x !== `spacer:${id}`)
      this.applySeq(seq, this.moreKeys, this.hiddenKeys)
    },

    // Set a divider's width in px (clamped). Backs the resize handle on the
    // header overlay and the −/+ / number control in the drawer. Mutates the
    // preview draft in place so only the affected divider repaints.
    setSpacerWidth(id, width) {
      let w = parseInt(width, 10)
      if (Number.isNaN(w)) w = DEFAULT_SPACER_WIDTH
      w = Math.max(MIN_SPACER_WIDTH, Math.min(MAX_SPACER_WIDTH, w))
      const items = this.effectiveItems.map((x) => ({ ...x }))
      const it = items.find((i) => i.key === 'spacer' && i.id === id)
      if (!it) return
      it.width = w
      this.setPreview({ items })
    },

    // Toggle a single control between visible-in-header and hidden (eye click).
    toggleHidden(key) {
      const snap = this.zonesSnapshot
      if (snap.hidden.includes(key)) {
        // Un-hide → append to the end of the header row (keep dividers).
        this.applySeq([...this.headerIids, key], snap.more, snap.hidden.filter((k) => k !== key))
      } else {
        if (this.metaOf(key).core) return // core items cannot be hidden
        const seq = this.headerIids.filter((x) => x !== key)
        this.applySeq(seq, snap.more.filter((k) => k !== key), [...snap.hidden, key])
      }
    },

    // Show this element directly in the top bar?
    inHeader(key) {
      if (!this.active) return true // legacy: everything inline
      const it = this._item(key)
      return !!it && it.visible && it.zone === 'header'
    },
    // Show this element inside the desktop "⋯" overflow menu?
    inMore(key) {
      if (!this.active) return false // legacy: no desktop overflow
      const it = this._item(key)
      return !!it && it.visible && it.zone === 'more'
    },
    // CSS flex order for a header-zone element.
    orderOf(key) {
      if (!this.active) return 0
      const it = this._item(key)
      return it ? it.order : 0
    },
    // Any items sitting in the "⋯" menu right now?
    hasMoreItems() {
      if (!this.active) return false
      return this.effectiveItems.some((it) => it.visible && it.zone === 'more')
    },

    async load() {
      if (this.loading) return
      this.loading = true
      try {
        const { data } = await headerBuilderApi.effective()
        if (data?.layout?.items) this.layout = data.layout
        if (Array.isArray(data?.catalog)) this.catalog = data.catalog
        this.source = data?.source || 'default'
        this.canEditCompany = !!data?.can_edit_company
        this.loaded = true
      } catch (e) {
        // Endpoint missing / offline → keep built-in default, do not disturb UI.
        this.loaded = true
        console.warn('[HeaderLayout] load failed, using default:', e?.response?.status || e?.message)
      } finally {
        this.loading = false
      }
    },

    setPreview(layout) {
      this.preview = layout ? { items: layout.items.map((x) => ({ ...x })) } : null
      this.previewVersion++
    },
    clearPreview() { this.preview = null; this.previewVersion++ },

    async save(scope, layout, userId = null) {
      const { data } = await headerBuilderApi.saveLayout(scope, layout, userId)
      // If we edited our own/effective scope, reflect immediately.
      if (data?.layout && (scope === 'me' || scope === 'company')) {
        this.layout = data.layout
        this.source = scope === 'me' ? 'user' : 'company'
      }
      this.clearPreview()
      return data
    },

    async reset(scope, userId = null) {
      await headerBuilderApi.resetLayout(scope, userId)
      this.clearPreview()
      await this.load()
    },
  },
})

export default useHeaderLayoutStore
